import api from './api';

const DATA_GOV_API = 'https://data.gov.il/api/3/action/datastore_search';
const CITIES_RESOURCE_ID = '5c78e9fa-c2e2-4771-93ff-7f400a12f7ba';
const STREETS_RESOURCE_ID = 'a7296d1a-f8c9-4b70-96c2-6ebb4352f8e3';

const CITY_FIELD = 'שם_ישוב';
const STREET_FIELD = 'שם_רחוב';

const CITIES_CACHE_KEY = 'israel_cities_cache_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

interface DataGovResponse {
  success: boolean;
  result?: {
    records: Record<string, string>[];
  };
}

let citiesMemoryCache: string[] | null = null;

async function fetchFromDataGov(
  resourceId: string,
  limit: number,
  q?: string
): Promise<Record<string, string>[]> {
  const searchParams = new URLSearchParams({
    resource_id: resourceId,
    limit: String(limit),
  });
  if (q) {
    searchParams.set('q', q);
  }

  const response = await fetch(`${DATA_GOV_API}?${searchParams.toString()}`);
  if (!response.ok) {
    throw new Error(`data.gov.il request failed: ${response.status}`);
  }
  const data: DataGovResponse = await response.json();
  if (!data.success || !data.result?.records) {
    throw new Error('Invalid data.gov.il response');
  }
  return data.result.records;
}

function readSessionCities(): string[] | null {
  try {
    const raw = sessionStorage.getItem(CITIES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; cities: string[] };
    if (Date.now() - parsed.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CITIES_CACHE_KEY);
      return null;
    }
    return parsed.cities;
  } catch {
    return null;
  }
}

function writeSessionCities(cities: string[]): void {
  try {
    sessionStorage.setItem(
      CITIES_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), cities })
    );
  } catch {
    // ignore quota errors
  }
}

export async function fetchCities(): Promise<string[]> {
  if (citiesMemoryCache) {
    return citiesMemoryCache;
  }

  const sessionCached = readSessionCities();
  if (sessionCached) {
    citiesMemoryCache = sessionCached;
    return sessionCached;
  }

  let records: Record<string, string>[];
  try {
    records = await fetchFromDataGov(CITIES_RESOURCE_ID, 32000);
  } catch {
    const proxyResponse = await api.get<{ data: string[] }>('/address/cities');
    const cities = proxyResponse.data.data;
    citiesMemoryCache = cities;
    writeSessionCities(cities);
    return cities;
  }

  const cities = [
    ...new Set(
      records
        .map((r) => r[CITY_FIELD])
        .filter((name): name is string => Boolean(name?.trim()))
    ),
  ].sort((a, b) => a.localeCompare(b, 'he'));

  citiesMemoryCache = cities;
  writeSessionCities(cities);
  return cities;
}

export async function fetchStreets(
  cityName: string,
  searchText?: string
): Promise<string[]> {
  if (!cityName.trim()) {
    return [];
  }

  const filter: Record<string, string> = { [CITY_FIELD]: cityName };
  if (searchText?.trim()) {
    filter[STREET_FIELD] = searchText.trim();
  }

  let records: Record<string, string>[];
  try {
    records = await fetchFromDataGov(STREETS_RESOURCE_ID, 50, JSON.stringify(filter));
  } catch {
    const proxyResponse = await api.get<{ data: string[] }>('/address/streets', {
      params: { city: cityName, q: searchText?.trim() || undefined },
    });
    return proxyResponse.data.data;
  }

  return [
    ...new Set(
      records
        .map((r) => r[STREET_FIELD])
        .filter((name): name is string => Boolean(name?.trim()))
    ),
  ].sort((a, b) => a.localeCompare(b, 'he'));
}
